import styled, { css } from "styled-components";
// Path assumes this file lives at
// src/components/sections/WelcomeScreen/Assessment/GoalStep.styles.js
import { DIET_C } from "../../../common/colors";

// ═══════════════════════════════════════════════════════════════════════════════
// GOAL STEP — card grid
// Selected state is a 2.4px emerald border only (no background fill) —
// keeps the interface calm per the shared assessment-screen spec.
// ═══════════════════════════════════════════════════════════════════════════════

export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--sp-4);

  @media (max-width: 60em) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 30em) {
    grid-template-columns: 1fr;
  }
`;
CardGrid.displayName = "CardGrid";

export const GoalCard = styled.button`
  background: var(--c-surface);
  border: 2.4px solid ${({ $active }) => ($active ? DIET_C.primary : `${DIET_C.secondary}66`)};
  border-radius: var(--r-sm);
  padding: var(--sp-6) var(--sp-3);
  min-height: 96px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  cursor: pointer;
  transition: border-color var(--t-fast), transform var(--t-fast), opacity var(--t-fast);

  &:hover {
    border-color: ${DIET_C.primary};
  }

  &:focus-visible {
    outline: 3px solid var(--c-accent);
    outline-offset: 2px;
  }

  ${({ $active }) =>
    $active &&
    css`
      box-shadow: 0 0 0 1px ${DIET_C.primary}22;
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.4;

      &:hover {
        border-color: ${DIET_C.secondary}66;
      }
    `}

  @media (max-width: 40em) {
    min-height: 84px;
    padding: var(--sp-5) var(--sp-3);
  }
`;
GoalCard.displayName = "GoalCard";

export const GoalCardLabel = styled.span`
  color: var(--c-text);
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.35;

  @media (max-width: 40em) {
    font-size: 0.9rem;
  }
`;
GoalCardLabel.displayName = "GoalCardLabel";

// ═══════════════════════════════════════════════════════════════════════════════
// INFO NOTE — e.g. explaining why some cards are disabled during pregnancy
// ═══════════════════════════════════════════════════════════════════════════════

export const InfoNote = styled.p`
  margin: var(--sp-4) 0 0;
  color: var(--c-text-muted);
  font-size: 0.85rem;
  line-height: 1.5;
`;
InfoNote.displayName = "InfoNote";

// ═══════════════════════════════════════════════════════════════════════════════
// SOFT WARNING — target weight vs. "Lose Weight" goal
// ═══════════════════════════════════════════════════════════════════════════════

export const WarningBox = styled.div`
  margin-top: var(--sp-5);
  background: ${DIET_C.accent}14;
  border: 1px solid ${DIET_C.accent}55;
  border-radius: var(--r-sm);
  padding: var(--sp-4) var(--sp-5);
`;
WarningBox.displayName = "WarningBox";

export const WarningText = styled.p`
  margin: 0 0 var(--sp-3);
  color: var(--c-text);
  font-size: 0.9rem;
  line-height: 1.5;
`;
WarningText.displayName = "WarningText";

export const WarningActions = styled.div`
  display: flex;
  gap: var(--sp-3);
  flex-wrap: wrap;
`;
WarningActions.displayName = "WarningActions";

export const WarningButton = styled.button`
  border-radius: var(--r-sm);
  padding: 9px 18px;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid ${DIET_C.secondary}88;
  background: var(--c-surface);
  color: var(--c-text);
  transition: background 0.15s ease;

  &:hover {
    background: ${DIET_C.secondary}22;
  }
`;
WarningButton.displayName = "WarningButton";

export const WarningButtonPrimary = styled(WarningButton)`
  background: var(--c-primary);
  color: #fff;
  border: none;

  &:hover {
    background: #26692a;
  }
`;
WarningButtonPrimary.displayName = "WarningButtonPrimary";